<template>
  <!-- Panel Mode: Sticky card for contract/delivery pages -->
  <div v-if="panelMode">
    <!-- When shouldShowFullscreen (fullScreen or filePreviewExpanded), wrap in v-dialog -->
    <v-dialog
      v-if="shouldShowFullscreen"
      :value="true"
      fullscreen
      persistent
      hide-overlay
      transition="none"
    >
      <v-card
        class="sinoa-panel d-flex flex-column panel-fullscreen"
        data-qa="sinoa-panel"
      >
        <!-- Expanded File Preview Mode: Only show FileViewer -->
        <template v-if="filePreviewExpanded">
          <div class="expanded-file-preview fill-height d-flex flex-column">
            <FileViewer
              :image-data="effectiveImageData"
              :file-type="effectiveFileType"
              :is-expanded="true"
              btn-size="24px"
              class="flex-grow-1"
              @toggle-full-screen="handleFileFullScreen"
            />
          </div>
        </template>

        <!-- Normal Fullscreen Mode: Show full chat interface -->
        <template v-else>
          <!-- Panel Header with Title and Actions -->
          <v-card-title
            class="sinoa-panel-header pa-4 justify-space-between white"
          >
            <SinoaTitle />
            <SinoaActions
              :full-screen="fullScreen"
              :showing-history="historyDrawer"
              :hide-close-button="true"
              @toggle-history="toggleHistoryDrawer"
              @toggle-fullscreen="toggleFullscreen"
            />
          </v-card-title>

          <v-divider />

          <!-- Panel Content Area -->
          <v-row
            no-gutters
            class="flex-grow-1 fill-height overflow-hidden flex-min-0"
          >
            <!-- History Drawer Column (when open in fullscreen) -->
            <v-col
              v-if="historyDrawer"
              :cols="$isMobile ? 12 : 4"
              class="d-flex flex-column fill-height overflow-hidden flex-min-0"
            >
              <template v-if="!$isMobile">
                <BreadcrumbGrainFoxHistory @back="historyDrawer = false" />
              </template>
              <ChatHistoryDrawer
                :history-drawer="historyDrawer"
                @show-answer="handleShowAnswer"
              />
            </v-col>

            <!-- Main Content Column -->
            <v-col
              :cols="historyDrawer && !$isMobile ? 8 : 12"
              class="d-flex flex-column fill-height overflow-hidden flex-min-0"
            >
              <v-card-text
                ref="panelChatContainer"
                class="sinoa-panel-content pa-4 flex-grow-1 overflow-y-auto"
              >
                <!-- File Preview Section (User Message Style) -->
                <div
                  v-if="hasContractImageData"
                  class="d-flex flex-column align-end mb-4 pr-2"
                >
                  <div
                    class="file-preview-bubble grey darken-1 rounded-lg rounded-br-0 pa-2"
                    style="max-width: 85%;"
                  >
                    <div
                      class="file-viewer-container file-viewer-container--fixed mb-2 rounded overflow-hidden white"
                    >
                      <FileViewer
                        :image-data="effectiveImageData"
                        :file-type="effectiveFileType"
                        btn-size="18px"
                        @toggle-full-screen="handleFileFullScreen"
                      />
                    </div>
                    <!-- File info cards for each attachment -->
                    <div
                      v-for="(attachment, index) in scannedAttachments"
                      :key="index"
                      class="file-info-card pa-2 rounded white d-flex align-center mt-2"
                    >
                      <v-icon
                        size="20"
                        :color="COLORS.grainfoxAIButton"
                        class="mr-2"
                      >
                        {{ getFileIcon(attachment) }}
                      </v-icon>
                      <div class="file-info flex-grow-1 overflow-hidden">
                        <p class="text-caption mb-0 font-weight-medium text-truncate">
                          {{ getFileName(attachment) }}
                        </p>
                        <p
                          class="text-caption grey--text mb-0"
                          style="font-size: 10px;"
                        >
                          {{ getFileSize(attachment) }}
                        </p>
                      </div>
                    </div>
                  </div>
                <!-- Timestamp-like placeholder if needed, or just leave as is -->
                </div>

                <ChatMessages
                  v-if="hasRealMessages"
                  class="mt-4"
                  :full-screen="false"
                  @scroll-bottom="scrollToBottomPanel"
                  @close-history="historyDrawer = false"
                  @followup-click="onFollowUp"
                />
              </v-card-text>

              <!-- Subscription Usage -->
              <v-card-text class="py-2 px-4 mt-auto">
                <v-divider class="mb-4" />
                <FrequentQuestionsCarousel
                  class="mb-4"
                  @faq-click="handleShowAnswer"
                />
                <SubscriptionChatUsage
                  :full-screen="false"
                  @buy-more-questions="handleBuyMoreQuestions"
                />
              </v-card-text>

              <!-- Input Section (paperclip conditionally hidden) -->
              <v-card-actions class="px-4 pb-4 pt-0">
                <CyclicPlaceholder
                  v-slot="{ text }"
                  class="d-flex flex-grow-1"
                >
                  <div
                    style="position: relative;"
                    class="d-flex flex-grow-1 align-center"
                  >
                    <v-text-field
                      ref="panelInputField"
                      v-model="userInput"
                      :placeholder="isStreaming ? '' : text"
                      hide-details
                      outlined
                      dense
                      rounded
                      :disabled="isSinoaLoading || isLimitReached || isStreaming"
                      :color="COLORS.grainfoxAIButton"
                      class="sinoa-input"
                      @keyup.enter="sendUserMessage"
                    >
                      <template v-slot:append>
                        <div class="d-flex align-center input-actions">
                          <!-- Paperclip hidden when hidePaperclip is true -->
                          <template v-if="showContractScanner && !hidePaperclip">
                            <GOnboardingTooltip
                              v-if="showContractScanner"
                              ref="panelAttachmentTooltip"
                              :value="showAttachmentTooltip"
                              position="left"
                              :background-color="COLORS.grainfoxAIButton"
                              use-close-icon
                              title=""
                              @ok="dismissAttachmentTooltip"
                            >
                              <template #title>
                                <p class="text-body-2 font-weight-medium white--text mb-2">
                                  Click here to scan contracts and auto-fill forms with extracted data.
                                </p>
                              </template>

                              <v-menu
                                offset-y
                                top
                                nudge-top="10"
                              >
                                <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                                  <v-btn
                                    icon
                                    small
                                    :disabled="isSinoaLoading || isLimitReached || isStreaming"
                                    v-bind="menuAttrs"
                                    v-on="menuOn"
                                  >
                                    <v-icon>mdi-paperclip</v-icon>
                                  </v-btn>
                                </template>

                                <v-list class="py-0">
                                  <v-list-item @click="handleAttachmentOption('scan-contract')">
                                    <v-list-item-icon>
                                      <v-icon>mdi-file-document-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>Scan Contract</v-list-item-title>
                                  </v-list-item>
                                </v-list>
                              </v-menu>
                            </GOnboardingTooltip>
                          </template>

                          <v-btn
                            icon
                            small
                            :disabled="isSinoaLoading || isLimitReached || isStreaming"
                            @click="sendUserMessage"
                          >
                            <v-icon>mdi-arrow-right-circle</v-icon>
                          </v-btn>
                        </div>
                      </template>
                    </v-text-field>

                    <div
                      v-if="isStreaming"
                      class="streaming-placeholder d-flex"
                    >
                      <span /><span /><span />
                    </div>
                  </div>
                </CyclicPlaceholder>
              </v-card-actions>

              <!-- Disclaimer -->
              <div class="px-4 pb-3">
                <SinoaDisclaimer />
              </div>
            </v-col>
          </v-row>
        </template>
      </v-card>
    </v-dialog>

    <!-- When not fullscreen, render as regular sticky card -->
    <v-card
      v-else
      class="sinoa-panel d-flex flex-column"
      :style="panelCardStyle"
      data-qa="sinoa-panel"
    >
      <!-- Panel Header with Title and Actions -->
      <v-card-title
        class="sinoa-panel-header pa-4 justify-space-between white"
      >
        <SinoaTitle />
        <SinoaActions
          :full-screen="fullScreen"
          :showing-history="historyDrawer"
          :hide-close-button="true"
          @toggle-history="toggleHistoryDrawer"
          @toggle-fullscreen="toggleFullscreen"
        />
      </v-card-title>

      <v-divider />

      <!-- Panel Content Area -->
      <v-row
        no-gutters
        class="flex-grow-1 fill-height overflow-hidden flex-min-0"
      >
        <!-- History Drawer Column (when open in fullscreen) -->
        <v-col
          v-if="historyDrawer && fullScreen"
          :cols="$isMobile ? 12 : 4"
          class="d-flex flex-column fill-height overflow-hidden flex-min-0"
        >
          <template v-if="!$isMobile">
            <BreadcrumbGrainFoxHistory @back="historyDrawer = false" />
          </template>
          <ChatHistoryDrawer
            :history-drawer="historyDrawer"
            @show-answer="handleShowAnswer"
          />
        </v-col>

        <!-- Main Content Column -->
        <v-col
          :cols="historyDrawer && fullScreen && !$isMobile ? 8 : 12"
          class="d-flex flex-column fill-height overflow-hidden flex-min-0"
        >
          <v-card-text
            ref="panelChatContainer"
            class="sinoa-panel-content pa-4 flex-grow-1 overflow-y-auto"
          >
            <!-- File Preview Section (User Message Style) -->
            <div
              v-if="hasContractImageData"
              class="d-flex flex-column align-end mb-4 pr-2"
            >
              <div
                class="file-preview-bubble grey darken-1 rounded-lg rounded-br-0 pa-2"
                style="max-width: 85%;"
              >
                <div
                  class="file-viewer-container file-viewer-container--fixed mb-2 rounded overflow-hidden white"
                >
                  <FileViewer
                    :image-data="effectiveImageData"
                    :file-type="effectiveFileType"
                    btn-size="18px"
                    @toggle-full-screen="handleFileFullScreen"
                  />
                </div>
                <!-- File info cards for each attachment -->
                <div
                  v-for="(attachment, index) in scannedAttachments"
                  :key="index"
                  class="file-info-card pa-2 rounded white d-flex align-center mt-2"
                >
                  <v-icon
                    size="20"
                    :color="COLORS.grainfoxAIButton"
                    class="mr-2"
                  >
                    {{ getFileIcon(attachment) }}
                  </v-icon>
                  <div class="file-info flex-grow-1 overflow-hidden">
                    <p class="text-caption mb-0 font-weight-medium text-truncate">
                      {{ getFileName(attachment) }}
                    </p>
                    <p
                      class="text-caption grey--text mb-0"
                      style="font-size: 10px;"
                    >
                      {{ getFileSize(attachment) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <ChatMessages
              v-if="hasRealMessages"
              class="mt-4"
              :full-screen="false"
              @scroll-bottom="scrollToBottomPanel"
              @close-history="historyDrawer = false"
              @followup-click="onFollowUp"
            />
          </v-card-text>

          <!-- Subscription Usage -->
          <v-card-text class="py-2 px-4 mt-auto">
            <v-divider class="mb-4" />
            <FrequentQuestionsCarousel
              class="mb-4"
              @faq-click="handleShowAnswer"
            />
            <SubscriptionChatUsage
              :full-screen="false"
              @buy-more-questions="handleBuyMoreQuestions"
            />
          </v-card-text>

          <!-- Input Section (paperclip conditionally hidden) -->
          <v-card-actions class="px-4 pb-4 pt-0">
            <CyclicPlaceholder
              v-slot="{ text }"
              class="d-flex flex-grow-1"
            >
              <div
                style="position: relative;"
                class="d-flex flex-grow-1 align-center"
              >
                <v-text-field
                  ref="panelInputField"
                  v-model="userInput"
                  :placeholder="isStreaming ? '' : text"
                  hide-details
                  outlined
                  dense
                  rounded
                  :disabled="isSinoaLoading || isLimitReached || isStreaming"
                  :color="COLORS.grainfoxAIButton"
                  class="sinoa-input"
                  @keyup.enter="sendUserMessage"
                >
                  <template v-slot:append>
                    <div class="d-flex align-center input-actions">
                      <!-- Paperclip hidden when hidePaperclip is true -->
                      <template v-if="showContractScanner && !hidePaperclip">
                        <GOnboardingTooltip
                          v-if="showContractScanner"
                          ref="panelAttachmentTooltip"
                          :value="showAttachmentTooltip"
                          position="left"
                          :background-color="COLORS.grainfoxAIButton"
                          use-close-icon
                          title=""
                          @ok="dismissAttachmentTooltip"
                        >
                          <template #title>
                            <p class="text-body-2 font-weight-medium white--text mb-2">
                              Click here to scan contracts and auto-fill forms with extracted data.
                            </p>
                          </template>

                          <v-menu
                            offset-y
                            top
                            nudge-top="10"
                          >
                            <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                              <v-btn
                                icon
                                small
                                :disabled="isSinoaLoading || isLimitReached || isStreaming"
                                v-bind="menuAttrs"
                                v-on="menuOn"
                              >
                                <v-icon>mdi-paperclip</v-icon>
                              </v-btn>
                            </template>

                            <v-list class="py-0">
                              <v-list-item @click="handleAttachmentOption('scan-contract')">
                                <v-list-item-icon>
                                  <v-icon>mdi-file-document-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Scan Contract</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </GOnboardingTooltip>
                      </template>

                      <v-btn
                        icon
                        small
                        :disabled="isSinoaLoading || isLimitReached || isStreaming"
                        @click="sendUserMessage"
                      >
                        <v-icon>mdi-arrow-right-circle</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-text-field>

                <div
                  v-if="isStreaming"
                  class="streaming-placeholder d-flex"
                >
                  <span /><span /><span />
                </div>
              </div>
            </CyclicPlaceholder>
          </v-card-actions>

          <!-- Disclaimer -->
          <div class="px-4 pb-3">
            <SinoaDisclaimer />
          </div>
        </v-col>
      </v-row>
    </v-card>
  </div>

  <!-- Expanded File Preview Overlay for Dialog Mode -->
  <v-dialog
    v-else-if="!panelMode && filePreviewExpanded"
    :value="true"
    fullscreen
    persistent
    hide-overlay
    transition="none"
  >
    <v-card class="d-flex flex-column panel-fullscreen">
      <div class="expanded-file-preview fill-height d-flex flex-column">
        <FileViewer
          :image-data="effectiveImageData"
          :file-type="effectiveFileType"
          :is-expanded="true"
          btn-size="24px"
          class="flex-grow-1"
          @toggle-full-screen="handleFileFullScreen"
        />
      </div>
    </v-card>
  </v-dialog>

  <!-- Existing Dialog/Bottom-Sheet Mode -->
  <component
    :is="component"
    v-else-if="!panelMode && !filePreviewExpanded"
    v-model="dialogValue"
    :fullscreen="fullScreen"
    inset
  >
    <v-card
      class="d-flex flex-column overflow-hidden rounded-b-0 white"
      :class="fullScreen ? 'rounded-0' : 'rounded-t-xl'"
      :style="{ height: $isMobile ? (fullScreen ? '100vh' : '85vh') : '85vh' }"
    >
      <v-card-title class="justify-space-between white">
        <SinoaTitle />
        <SinoaActions
          :full-screen="fullScreen"
          :showing-history="historyDrawer"
          @toggle-history="toggleHistoryDrawer"
          @toggle-fullscreen="toggleFullscreen"
          @close-dialog="closeDialog"
        />
      </v-card-title>

      <v-divider />

      <v-row
        no-gutters
        class="flex-grow-1 fill-height overflow-hidden flex-min-0"
      >
        <v-col
          v-if="historyDrawer"
          :cols="$isMobile ? 12 : 4"
          class="d-flex flex-column fill-height overflow-hidden flex-min-0"
        >
          <template v-if="!$isMobile">
            <BreadcrumbGrainFoxHistory
              @back="historyDrawer = false"
            />
          </template>

          <ChatHistoryDrawer
            :history-drawer="historyDrawer"
            @show-answer="handleShowAnswer"
          />
        </v-col>

        <v-col
          :cols="historyDrawer && !$isMobile ? 8 : 12"
          class="d-flex flex-column fill-height overflow-hidden flex-min-0"
        >
          <div class="d-flex flex-column fill-height overflow-hidden flex-min-0">
            <v-card-text
              ref="chatContainer"
              class="chat-container overflow-y-auto flex-grow-1 flex-min-0"
              :class="[
                $isMobile ? 'px-6' : 'px-8',
                { 'full-screen': fullScreen }
              ]"
            >
              <!-- File Preview Section for Contract Scanner (User Message Style) -->
              <div
                v-if="hasContractImageData"
                class="d-flex flex-column align-end mb-4 pr-2"
              >
                <div
                  class="file-preview-bubble grey darken-1 rounded-lg rounded-br-0 pa-2"
                  style="max-width: 85%;"
                >
                  <div
                    class="file-viewer-container file-viewer-container--fixed mb-2 rounded overflow-hidden white"
                  >
                    <FileViewer
                      :image-data="effectiveImageData"
                      :file-type="effectiveFileType"
                      btn-size="18px"
                      @toggle-full-screen="handleFileFullScreen"
                    />
                  </div>
                  <!-- File info cards for each attachment -->
                  <div
                    v-for="(attachment, index) in scannedAttachments"
                    :key="index"
                    class="file-info-card pa-2 rounded white d-flex align-center mt-2"
                  >
                    <v-icon
                      size="20"
                      :color="COLORS.grainfoxAIButton"
                      class="mr-2"
                    >
                      {{ getFileIcon(attachment) }}
                    </v-icon>
                    <div class="file-info flex-grow-1 overflow-hidden">
                      <p class="text-caption mb-0 font-weight-medium text-truncate">
                        {{ getFileName(attachment) }}
                      </p>
                      <p
                        class="text-caption grey--text mb-0"
                        style="font-size: 10px;"
                      >
                        {{ getFileSize(attachment) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="!hasRealMessages">
                <div
                  v-if="showPaywall"
                  class="paywall-card"
                >
                  <Paywall />
                </div>
                <div
                  v-else
                  class="d-flex flex-column justify-center align-center text-center"
                >
                  <SinoaWelcome />
                </div>
              </div>
              <div v-else>
                <ChatMessages
                  class="mt-4"
                  :full-screen="fullScreen"
                  @scroll-bottom="scrollToBottom"
                  @close-history="historyDrawer = false"
                  @followup-click="onFollowUp"
                />
              </div>
            </v-card-text>

            <v-card-text
              class="py-2 mt-auto"
              :class="$isMobile ? 'px-6' : 'px-8'"
            >
              <v-divider class="mb-4" />
              <FrequentQuestionsCarousel
                class="mb-4"
                @faq-click="handleShowAnswer"
              />
              <SubscriptionChatUsage
                :key="`chat-usage-${fullScreen}`"
                :full-screen="fullScreen"
                @buy-more-questions="handleBuyMoreQuestions"
              />
            </v-card-text>

            <v-card-actions class="px-6 pb-3 pt-0">
              <CyclicPlaceholder
                v-slot="{ text }"
                class="d-flex flex-grow-1"
              >
                <div
                  style="position: relative;"
                  class="d-flex flex-grow-1 align-center"
                >
                  <v-text-field
                    ref="inputField"
                    v-model="userInput"
                    :placeholder="isStreaming ? '' : text"
                    hide-details
                    outlined
                    dense
                    rounded
                    :disabled="isSinoaLoading || isLimitReached || isStreaming"
                    :color="COLORS.grainfoxAIButton"
                    class="sinoa-input"
                    @keyup.enter="sendUserMessage"
                  >
                    <template v-slot:append>
                      <div
                        class="d-flex align-center input-actions"
                      >
                        <GOnboardingTooltip
                          v-if="showContractScanner"
                          ref="attachmentTooltip"
                          :value="showAttachmentTooltip"
                          position="left"
                          :background-color="COLORS.grainfoxAIButton"
                          :style="tooltipStyle"
                          use-close-icon
                          title=""
                          @ok="dismissAttachmentTooltip"
                        >
                          <template #title>
                            <p class="text-body-2 font-weight-medium white--text mb-2">
                              Click here to scan contracts and auto-fill forms with extracted data.
                            </p>
                          </template>

                          <v-tooltip
                            top
                            :open-delay="2000"
                            :color="COLORS.grainfoxAIButton"
                            :disabled="showAttachmentTooltip"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <div
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-menu
                                  offset-y
                                  top
                                  nudge-top="10"
                                >
                                  <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                                    <v-btn
                                      icon
                                      small
                                      :disabled="isSinoaLoading || isLimitReached || isStreaming"
                                      v-bind="menuAttrs"
                                      v-on="menuOn"
                                    >
                                      <v-icon>
                                        mdi-paperclip
                                      </v-icon>
                                    </v-btn>
                                  </template>

                                  <v-list class="py-0">
                                    <v-list-item @click="handleAttachmentOption('scan-contract')">
                                      <v-list-item-icon>
                                        <v-icon>mdi-file-document-outline</v-icon>
                                      </v-list-item-icon>
                                      <v-list-item-title>Scan Contract</v-list-item-title>
                                    </v-list-item>
                                  </v-list>
                                </v-menu>
                              </div>
                            </template>
                            <span class="white--text font-weight-bold">
                              Click here to scan contracts and auto-fill forms with extracted data.
                            </span>
                          </v-tooltip>
                        </GOnboardingTooltip>

                        <v-btn
                          icon
                          small
                          :disabled="isSinoaLoading || isLimitReached || isStreaming"
                          @click="sendUserMessage"
                        >
                          <v-icon>
                            mdi-arrow-right-circle
                          </v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-text-field>

                  <div
                    v-if="isStreaming"
                    class="streaming-placeholder d-flex"
                  >
                    <span /><span /><span />
                  </div>
                </div>
              </CyclicPlaceholder>
            </v-card-actions>

            <div class="pb-6">
              <SinoaDisclaimer />
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <SinoaContractScanner v-model="showContractScannerModal" />
  </component>
</template>

<script>
import { COLORS } from '../../helpers/colors';
import VBottomSheet from 'vuetify/lib/components/VBottomSheet';
import FrequentQuestionsCarousel from './FrequentQuestionsCarousel.vue';
import SubscriptionChatUsage from './SubscriptionChatUsage.vue';
import SinoaTitle from './SinoaTitle.vue';
import SinoaWelcome from './SinoaWelcome.vue';
import ChatMessages from './ChatMessages.vue';
import ChatHistoryDrawer from './ChatHistoryDrawer.vue';
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import BreadcrumbGrainFoxHistory from './BreadcrumbGrainFoxHistory.vue';
import CyclicPlaceholder from './CyclicPlaceholder.vue';
import Paywall from './Paywall.vue';
import { datadogRum } from '@datadog/browser-rum';
import SinoaActions from './SinoaActions.vue';
import SinoaDisclaimer from './SinoaDisclaimer.vue';
import SinoaContractScanner from './SinoaContractScanner.vue';
import GOnboardingTooltip from '@/components/generic/GOnboardingTooltip.vue';
import FileViewer from '@/components/viewer/FileViewer.vue';

export default {
  name: 'GrainFoxAssistantDialog',

  components: {
    VBottomSheet,
    FrequentQuestionsCarousel,
    SubscriptionChatUsage,
    SinoaTitle,
    SinoaWelcome,
    ChatMessages,
    ChatHistoryDrawer,
    BreadcrumbGrainFoxHistory,
    CyclicPlaceholder,
    Paywall,
    SinoaActions,
    SinoaDisclaimer,
    SinoaContractScanner,
    GOnboardingTooltip,
    FileViewer,
  },

  props: {
    /**
     * When true, renders as a sticky panel (v-card) instead of dialog/bottom-sheet
     * Used in contract/delivery pages when coming from Sinoa scanner
     * @type {boolean}
     */
    panelMode: {
      type: Boolean,
      default: false,
    },

    /**
     * Type of item being scanned - 'contract' or 'delivery'
     * Used for response message context. Only applicable in panelMode.
     * @type {string|null}
     */
    scanType: {
      type: String,
      default: null,
      validator: (value) => value === null || ['contract', 'delivery'].includes(value),
    },

    /**
     * Whether to hide the paperclip/attachment button
     * Set to true when already in contract scanner flow
     * @type {boolean}
     */
    hidePaperclip: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      fullScreen: false,
      historyDrawer: false,
      COLORS,
      userInput: '',
      showContractScanner: false,
      showContractScannerModal: false,
      showAttachmentTooltip: false,
      /** Generated image data from attachment file for FileViewer */
      generatedImageData: null,
      /** Generated file type from attachment */
      generatedFileType: null,
      /** Whether file preview is expanded to full screen */
      filePreviewExpanded: false,
    };
  },

  computed: {
    ...mapState('chatbot', ['isGrainFoxAssistantDialogOpen', 'messages', 'isLoading', 'showPaywall', 'showFaqs']),
    ...mapState('app', ['isMimic']),
    ...mapState('launchDarkly', ['flags']),
    ...mapState('contractScanner', [
      'scannedAttachments',
      'sinoaResponseMessage',
      'contractImageData',
      'salesContractFileType',
    ]),
    ...mapGetters('chargebee', ['isCommercial']),
    ...mapGetters('chatbot', ['hasRealMessages', 'isLimitReached', 'isSinoaLoading', 'isStreaming']),
    ...mapGetters('permissions', ['checkPermission']),

    dialogValue: {
      get() {
        return this.isGrainFoxAssistantDialogOpen;
      },
      set(val) {
        this.setGrainFoxAssistantDialogOpen(val);
      },
    },

    component() {
      return this.fullScreen ? 'v-dialog' : 'v-bottom-sheet';
    },

    anyGatedArticleQna() {
      return (this.isLimitReached || this.showPaywall)
        && Array.isArray(this.messages)
        && this.messages.some((m) => m?.question_type === 'qna');
    },

    anyOverlayGated() {
      return (this.isLimitReached || this.showPaywall)
        && Array.isArray(this.messages)
        && this.messages.some((m) => m?.is_overlay_gated === true);
    },

    hasContractImageData() {
      return !!(this.contractImageData || this.generatedImageData);
    },

    effectiveImageData() {
      return this.contractImageData || this.generatedImageData;
    },

    /**
     * Effective file type for FileViewer (from store or generated)
     * @returns {string|null}
     */
    effectiveFileType() {
      return this.salesContractFileType || this.generatedFileType;
    },

    /**
     * Panel card styling
     * @returns {object}
     */
    panelCardStyle() {
      return {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        height: 'calc(100vh - 140px)',
        overflow: 'hidden',
      };
    },

    hasScannedAttachments() {
      return !!(this.scannedAttachments && this.scannedAttachments.length > 0);
    },

    /**
     * Whether to show fullscreen mode (either dialog fullscreen or file preview expanded)
     * @returns {boolean}
     */
    shouldShowFullscreen() {
      return this.fullScreen || this.filePreviewExpanded;
    },
  },

  async mounted() {
    this.updateFaqVisibility();
    await this.checkContractScannerFeature();
    this.checkAttachmentTooltipDismissal();
    await this.updateTooltipPosition();
  },

  watch: {
    dialogValue(newVal) {
      if (newVal) {
        this.fetchDeepDiveSettings();
        if (!this.showPaywall && !this.$isMobile) {
          this.$nextTick(() => this.focusInput());
        }
        // Trigger tooltip position recalculation when dialog opens
        this.$nextTick(() => {
          this.updateTooltipPosition();
        });
      }
    },

    isSinoaLoading(newVal) {
      if (!newVal && !this.$isMobile) {
        this.$nextTick(() => this.focusInput());
      }
    },

    messages: {
      handler() {
        this.$nextTick(() => {
          if (!this.anyGatedArticleQna && !this.anyOverlayGated) {
            this.scrollToBottom();
            if (!this.$isMobile) {
              this.focusInput();
            }
          }
        });
      },
      deep: false,
    },

    hasRealMessages() {
      this.$nextTick(() => this.updateFaqVisibility());
    },

    showPaywall() {
      this.$nextTick(() => {
        this.updateFaqVisibility();
        if (!this.anyGatedArticleQna && !this.anyOverlayGated) {
          this.scrollToBottom();
        }
      });
    },

    fullScreen() {
      this.$nextTick(() => {
        this.updateTooltipPosition();
      });
    },

    historyDrawer() {
      this.$nextTick(() => {
        this.updateTooltipPosition();
      });
    },

    showAttachmentTooltip(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.updateTooltipPosition();
        });
      }
    },

    /**
     * Watch for scannedAttachments changes to generate image data when available
     */
    scannedAttachments: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.contractImageData) {
          this.generateImageDataFromAttachment(newVal[0]);
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions('chatbot', ['sendMessage', 'showStoredAnswer', 'fetchDeepDiveSettings']),
    ...mapActions('contractScanner', ['loadAttachmentsFromHistory']),
    ...mapMutations('chatbot', ['setGrainFoxAssistantDialogOpen', 'setFollowUpQuestions', 'setShowFaqs', 'setShowPaywall']),

    updateFaqVisibility() {
      // Hide FAQs once there are real messages (FAQ/QnA engaged), even if a paywall is visible
      if (this.hasRealMessages) {
        this.setShowFaqs(false);
        return;
      }

      const shouldShow = this.$isMobile
        ? this.showPaywall
        : (!this.hasRealMessages || this.showPaywall);

      this.setShowFaqs(shouldShow);
    },

    async sendUserMessage() {
      const question = this.userInput.trim();
      if (!question) return;

      this.userInput = '';
      await this.sendMessage({ question });
      this.updateFaqVisibility();
      datadogRum.addAction('Sinoa: Send Message', {
        source: 'GrainFoxAssistantDialog',
        isMobile: this.$isMobile ?? null,
        isMimic: this.isMimic,
      });
    },

    closeDialog() {
      this.fullScreen = false;
      this.historyDrawer = false;
      this.setGrainFoxAssistantDialogOpen(false);
    },

    toggleFullscreen() {
      this.fullScreen = !this.fullScreen;
      if (!this.fullScreen) this.historyDrawer = false;
    },

    toggleHistoryDrawer() {
      this.fullScreen = true;
      this.historyDrawer = !this.historyDrawer;
    },

    scrollToBottom() {
      if (this.$refs.chatContainer) {
        this.$nextTick(() => {
          this.$refs.chatContainer.scrollTop = this.$refs.chatContainer.scrollHeight;
        });
      }
    },

    focusInput() {
      this.$nextTick(() => {
        this.$refs.inputField?.focus?.();
      });
    },

    handleShowAnswer(item) {
      this.showStoredAnswer(item);
      if (item?.attachments?.length > 0) {
        this.loadAttachmentsFromHistory(item.attachments);
      }
      if (this.$isMobile) {
        this.historyDrawer = false;
      }
    },

    async onFollowUp(question) {
      this.setFollowUpQuestions([]);
      await this.sendMessage({ question });
    },

    handleBuyMoreQuestions() {
      this.setShowPaywall(true);
      this.updateFaqVisibility();
    },

    async checkContractScannerFeature() {
      try {
        const hasPermissions = this.checkPermission('ai_chatbot', 'read')
          && this.checkPermission('farm_profile', 'create');
        this.showContractScanner = hasPermissions && this.flags['sinoa-file-upload'] && !this.isCommercial;
      } catch (error) {
        this.showContractScanner = false;
      }
    },

    handleAttachmentOption(type) {
      if (type === 'scan-contract') {
        this.showContractScannerModal = true;
      }
    },

    checkAttachmentTooltipDismissal() {
      const dismissedDate = localStorage.getItem('attachment-tooltip-dismissed');
      const today = new Date().toISOString().split('T')[0];

      if (dismissedDate !== today) {
        this.showAttachmentTooltip = true;
      }
    },

    dismissAttachmentTooltip() {
      const today = new Date().toISOString().split('T')[0];
      localStorage.setItem('attachment-tooltip-dismissed', today);
      this.showAttachmentTooltip = false;
    },

    tooltipStyle() {
      return 'z-index: 10000';
    },

    async updateTooltipPosition() {
      // Only recalculate if tooltip is visible and dialog is open
      if (!this.showAttachmentTooltip || !this.dialogValue) {
        return;
      }

      // Use multiple delayed recalculations to handle async layout changes
      const intervals = [0, 100, 300, 500];

      intervals.forEach((delay) => {
        setTimeout(() => {
          this.$nextTick(() => {
            // Check if tooltip ref exists and tooltip is still visible
            if (this.$refs.attachmentTooltip && this.showAttachmentTooltip) {
              try {
                this.$refs.attachmentTooltip.recomputeAnchorPosition();
                this.$refs.attachmentTooltip.recomputeContentSize();
              } catch (error) {
                // Silently fail if methods don't exist or throw errors
              }
            }
          });
        }, delay);
      });
    },

    /**
     * Get appropriate icon for file type
     * @param {object} attachment - The attachment object
     * @returns {string} Material Design icon name
     */
    getFileIcon(attachment) {
      const file = attachment?.file;
      if (!file) return 'mdi-file-outline';
      const type = file.type || '';
      if (type.includes('pdf')) return 'mdi-file-pdf-box';
      if (type.includes('image')) return 'mdi-file-image-outline';
      if (type.includes('excel') || type.includes('spreadsheet')) return 'mdi-file-excel-outline';
      if (type.includes('word') || type.includes('document')) return 'mdi-file-word-outline';
      return 'mdi-file-document-outline';
    },

    /**
     * Get file name from attachment
     * @param {object} attachment - The attachment object
     * @returns {string}
     */
    getFileName(attachment) {
      return attachment?.file?.name || 'Unknown file';
    },

    /**
     * Get formatted file size
     * @param {object} attachment - The attachment object
     * @returns {string}
     */
    getFileSize(attachment) {
      const size = attachment?.file?.size;
      if (!size) return '';
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    },

    /**
     * Handle full screen toggle event from FileViewer
     * Toggles the file preview expanded state
     */
    handleFileFullScreen() {
      this.filePreviewExpanded = !this.filePreviewExpanded;
    },

    /**
     * Scroll panel chat container to bottom
     */
    scrollToBottomPanel() {
      if (this.$refs.panelChatContainer) {
        this.$nextTick(() => {
          this.$refs.panelChatContainer.scrollTop = this.$refs.panelChatContainer.scrollHeight;
        });
      }
    },

    /**
     * Generate image data from attachment file for FileViewer
     * Handles both File objects (with arrayBuffer method) and pre-processed data from history
     * @param {object} attachment - The attachment object
     */
    async generateImageDataFromAttachment(attachment) {
      const file = attachment?.file;
      if (!file) return;

      try {
        // Check if file has arrayBuffer method (actual File object)
        if (typeof file.arrayBuffer === 'function') {
          const arrayBuffer = await file.arrayBuffer();
          this.generatedImageData = arrayBuffer;
          this.generatedFileType = file.type || '';
        } else if (attachment.data) {
          // Handle pre-processed data from history (ArrayBuffer or base64)
          this.generatedImageData = attachment.data;
          this.generatedFileType = attachment.fileType || file.type || '';
        }
        // If neither case, attachment is not processable - skip silently
      } catch (error) {
        // Silently fail - attachment may not be processable
        // eslint-disable-next-line no-console
        console.error('Failed to generate image data from attachment:', error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/colors/colors.scss";

.flex-min-0 {
  min-height: 0;
}

.chat-container {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
  flex: 1 1 auto;

  @media (max-width: 600px) {
    &.full-screen {
      max-height: calc(100vh - 200px);
    }
  }
}

::v-deep .sinoa-input {
  .v-input__append-inner {
    margin-top: 0 !important;
    align-self: center;
    padding-right: 0 !important;
  }

  .input-actions {
    margin-right: -14px;
  }

  &.v-input--is-focused .input-actions .v-icon {
    color: #12A8C4 !important;
  }

  .input-actions .v-btn--disabled .v-icon {
    color: rgba(0, 0, 0, 0.2) !important;
  }
}

// Reduce spacing between icon and text in attachment menu
::v-deep .v-list-item__icon {
  margin-right: 8px !important;
}

::v-deep .v-dialog.v-dialog--fullscreen .v-card {
  max-width: 60vw !important;
  @media (max-width: 600px) {
    max-width: 100vw !important;
  }
}

// Prevent attachment tooltip from overflowing
::v-deep .g-onboarding-tooltip__content {
  max-width: 280px !important;
  border-radius: 16px !important;
}

.paywall-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 16px;
  box-sizing: border-box;
  width: 100%;
  max-width: 720px;
  margin: 16px auto;
}

@media (max-width: 600px) {
  .paywall-card {
    padding: 8px;
  }
}

.streaming-placeholder {
  position: absolute;
  top: 50%;
  left: 5%;
  transform: translateY(-50%);
  gap: 4px;
  pointer-events: none;
}

.streaming-placeholder span {
  width: 6px;
  height: 6px;
  background: colors.$primaryGrey;
  border-radius: 50%;
  animation: blink 1.4s infinite both;
}

.streaming-placeholder span:nth-child(2) {
  animation-delay: 0.2s;
}

.streaming-placeholder span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes blink {
  0% { opacity: 0.2; transform: scale(1); }
  20% { opacity: 1; transform: scale(1.3); }
  100% { opacity: 0.2; transform: scale(1); }
}

// Panel mode styles
.sinoa-panel {
  max-height: calc(100vh - 170px);
  overflow: hidden;
  border-radius: 8px !important;

  &.v-card {
    border-radius: 8px !important;
  }
}

.sinoa-panel-header {
  border-radius: 6px 6px 0 0;
  margin: -1px;
  margin-bottom: 0;
}

.sinoa-panel-content {
  background-color: #fafafa;
  min-height: 200px;
}

.file-preview-section {
  $preview-height: min(320px, 35vh);

  .file-viewer-container {
    background-color: white;
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    max-height: $preview-height;
    overflow: hidden;

    ::v-deep .file-viewer {
      height: $preview-height;
      padding-top: 0;

      .contract-image {
        height: $preview-height !important;
        max-height: $preview-height !important;
      }

      .contract-image-controls-container {
        bottom: 20px;
      }
    }
  }

  .file-info-card {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    margin-top: 8px;

    .file-info {
      overflow: hidden;
    }
  }
}

.panel-fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  max-height: 100vh !important;
  z-index: 9999 !important; /* Above other modals */
  border-radius: 0 !important;
  margin: 0 !important;
}

// Fixed file viewer container - prevents zoom from affecting message bubble width
.file-viewer-container--fixed {
  width: 400px;
  max-width: 100%;
  height: 250px;
  max-height: 250px;
  position: relative;

  ::v-deep .file-viewer {
    height: 100%;
    width: 100%;

    .contract-image {
      height: 100% !important;
      max-height: 100% !important;
      width: 100% !important;
      overflow: auto;
    }

    .contract-image-controls-container {
      bottom: 10px;
    }
  }
}

// Expanded file preview mode styles
.expanded-file-preview {
  width: 100%;
  height: 100%;
  background-color: #fafafa;
  position: relative;

  ::v-deep .file-viewer {
    height: 100%;
    width: 100%;
    padding: 60px 24px 24px;

    .contract-image {
      height: calc(100vh - 150px) !important;
      max-height: calc(100vh - 150px) !important;
      width: 100% !important;
      overflow: auto;
      border-radius: 8px;
    }

    .contract-image-controls-container {
      bottom: 40px;
    }
  }

  // Mobile styles for expanded preview
  @media (max-width: 600px) {
    ::v-deep .file-viewer {
      padding: 40px 12px 12px;

      .contract-image {
        height: calc(100vh - 120px) !important;
        max-height: calc(100vh - 120px) !important;
      }

      .contract-image-controls-container {
        bottom: 24px;
      }
    }
  }
}
</style>
